// Small helpers so the pages read like the markup they produce. No framework: the prototype proved
// this design works in plain DOM, and one dependency fewer is one thing fewer to keep current.

export const $ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T | null => root.querySelector<T>(sel);
export const $$ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T[] => [...root.querySelectorAll<T>(sel)];

/** Escape anything that came from a person or the backend before it goes into HTML. */
export const esc = (s: unknown): string =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

export const aed = (n: number): string => `AED ${Math.round(n).toLocaleString("en-AE")}`;
export const cap = (s: string): string => (s ? s[0]!.toUpperCase() + s.slice(1) : s);
export const first = (name: string): string => name.trim().split(/\s+/)[0] ?? name;
export const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function html(strings: TemplateStringsArray, ...values: unknown[]): string {
  return strings.reduce((out, s, i) => out + s + (i < values.length ? String(values[i] ?? "") : ""), "");
}

export function mount(markup: string): HTMLElement {
  const main = $("#app")!;
  main.innerHTML = markup;
  window.scrollTo(0, 0);
  return main as HTMLElement;
}

/** A face-shaped placeholder while a tutor hasn't added a photo. Never a stock photo of someone else. */
export function initialsAvatar(name: string, size = 120): string {
  const hue = [...name].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 7);
  const initials = name.split(/\s+/).slice(0, 2).map((w) => w[0]).join("");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120" width="${size}" height="${size}"><rect width="120" height="120" fill="hsl(${hue} 45% 88%)"/><text x="60" y="74" text-anchor="middle" font-family="system-ui, sans-serif" font-size="42" font-weight="700" fill="hsl(${hue} 40% 34%)">${esc(initials)}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

let toastTimer: number | undefined;
export function toast(message: string): void {
  let el = $("#toast");
  if (!el) {
    el = document.createElement("div");
    el.id = "toast";
    el.className = "toast";
    el.setAttribute("role", "status");
    document.body.append(el);
  }
  el.textContent = message;
  el.classList.add("show");
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => el!.classList.remove("show"), 4000);
}

/** Every async screen gets the same three states, so nothing ever shows a blank page. */
export const loading = (what: string) => html`<div class="loading"><span class="pulse" aria-hidden="true"></span><p>${esc(what)}</p></div>`;
export const failed = (message: string, retry = true) => html`
  <div class="empty">
    <h3>That didn't work</h3>
    <p>${esc(message)}</p>
    ${retry ? `<button class="btn" type="button" onclick="location.reload()">Try again</button>` : ""}
    <p class="soft small">Still stuck? Message us on WhatsApp and a person will sort it out.</p>
  </div>`;
